import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Logo, SectionLabel } from "@/components/bits";
import { BANNER } from "@/lib/images";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agreed) { setErr("You must accept the Terms of Service to create an account."); return; }
    setLoading(true);
    try {
      await register(email, password, name, agreed);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Registration failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center px-5 py-24 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="lg:hidden mb-10"><Logo size={40} /></div>
          <SectionLabel>NEW OPERATIVE</SectionLabel>
          <h1 className="mt-4 font-display text-4xl uppercase">Create Account</h1>

          {err && <div className="mt-6 rounded-xl border border-white/30 bg-white/[0.05] px-4 py-3 text-sm text-white/80" data-testid="register-error">{err}</div>}

          <div className="mt-8 space-y-5">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">USERNAME</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm focus:border-white focus:outline-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">EMAIL</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm focus:border-white focus:outline-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">PASSWORD</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm focus:border-white focus:outline-none" />
              <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-white/30">MIN 6 CHARACTERS</p>
            </div>
          </div>

          <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="register-tos-label">
            <input
              type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              data-testid="register-tos-checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-white"
            />
            <span className="text-xs text-white/55 leading-relaxed">
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" className="text-white underline">Terms of Service</Link>.
            </span>
          </label>

          <button type="submit" disabled={loading || !agreed} data-testid="register-submit" className="btn-inj mt-6 w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? <><Loader2 size={16} className="animate-spin" /> CREATING</> : "CREATE ACCOUNT"}
          </button>

          <p className="mt-6 text-center text-sm text-white/50">
            Already have an account? <Link to="/login" className="text-white underline" data-testid="to-login-link">Log in</Link>
          </p>
        </form>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={BANNER} alt="" className="absolute inset-0 h-full w-full object-cover grayscale-img" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative h-full flex flex-col justify-between p-14 text-right items-end">
          <Logo size={44} />
          <div>
            <h2 className="font-display text-5xl uppercase leading-[0.9]">Join the<br />elite.</h2>
            <p className="mt-4 max-w-sm text-white/50 ml-auto">Instant delivery, a personal dashboard, and 24/7 support the moment you sign up.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
